import type { DistributionEntry } from "@spoonity/flags-contract";

export function distributionSum(distribution: DistributionEntry[]): number {
  return distribution.reduce((sum, e) => sum + (Number.isFinite(e.percent) ? e.percent : 0), 0);
}

export function distributionErrors(distribution: DistributionEntry[], treatmentNames: string[]): string[] {
  const errors: string[] = [];
  if (distribution.length === 0) {
    errors.push("at least one treatment is required");
    return errors;
  }
  const sum = distributionSum(distribution);
  if (sum !== 100) errors.push(`percentages must sum to 100 (currently ${sum})`);
  const seen = new Set<string>();
  for (const entry of distribution) {
    if (!entry.treatment) {
      errors.push("every row needs a treatment");
    } else if (!treatmentNames.includes(entry.treatment)) {
      errors.push(`"${entry.treatment}" is not a declared treatment`);
    }
    if (seen.has(entry.treatment)) errors.push(`"${entry.treatment}" appears twice`);
    seen.add(entry.treatment);
  }
  return errors;
}

/** Per-treatment percentage inputs that must sum to 100, with a live indicator (task brief). */
export function DistributionEditor({
  distribution,
  treatmentNames,
  onChange,
  readOnly,
  label,
}: {
  distribution: DistributionEntry[];
  treatmentNames: string[];
  onChange: (next: DistributionEntry[]) => void;
  readOnly?: boolean;
  label?: string;
}) {
  const sum = distributionSum(distribution);
  const errors = distributionErrors(distribution, treatmentNames);

  function update(index: number, patch: Partial<DistributionEntry>) {
    const next = distribution.map((e, i) => (i === index ? { ...e, ...patch } : e));
    onChange(next);
  }

  function remove(index: number) {
    onChange(distribution.filter((_, i) => i !== index));
  }

  function add() {
    const unused = treatmentNames.find((n) => !distribution.some((e) => e.treatment === n));
    onChange([...distribution, { treatment: unused ?? treatmentNames[0] ?? "", percent: 0 }]);
  }

  return (
    <div>
      {label && <div className="hint" style={{ marginBottom: "var(--space-1)" }}>{label}</div>}
      {distribution.map((entry, i) => (
        <div className="distribution-row" key={i}>
          <select
            className="select"
            value={entry.treatment}
            disabled={readOnly}
            aria-label={`distribution treatment ${i + 1}`}
            onChange={(e) => update(i, { treatment: e.target.value })}
          >
            <option value="" disabled>
              choose…
            </option>
            {treatmentNames.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <input
            className="select"
            type="number"
            min={0}
            max={100}
            value={entry.percent}
            disabled={readOnly}
            aria-label={`distribution percent ${i + 1}`}
            onChange={(e) => update(i, { percent: Number(e.target.value) })}
          />
          {!readOnly && (
            <button type="button" className="btn small danger" onClick={() => remove(i)} aria-label="Remove row">
              Remove
            </button>
          )}
        </div>
      ))}
      {!readOnly && (
        <button type="button" className="btn small" onClick={add}>
          Add treatment
        </button>
      )}
      <div className={`percent-indicator ${sum === 100 ? "ok" : "bad"}`} data-testid="distribution-sum">
        Total: {sum}%
      </div>
      {errors.map((e) => (
        <div key={e} className="error">
          {e}
        </div>
      ))}
    </div>
  );
}
